import { useTheme } from '@mui/material';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useVarietiesByCustomer } from '../../../../services/entities/hooks/useVarietiesByCustomer';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { getUnitSymbol, MeasurementTypes } from '../../../commonUtils';
import {
  RanchPageViewEnum,
  selectIsLoadingCustomerSummary,
  setRanchPageView,
} from '../../../ranches/ranchesSlice';
import {
  selectCurrCustomerID,
  setCurrBlockByID,
  setCurrRanchByID,
  setCurrScanByID,
} from '../../../select/selectSlice';
import { useUnits } from '../../../settings/hooks/useUnits';
import {
  addRanchSummaryClickedRowScanID,
  selectRanchSummaryClickedRowScanIDs,
  selectRanchSummaryTableMeasure,
  setRanchSummaryTableNumRowsSelected,
} from '../../dataGridTablesSlice';
import GenericDataGrid from '../../GenericDataGrid';
import { createExportFileName } from '../../utils/createExportFileName';
import NoRowsOverlayRanchSummary from '../NoRowsOverlayRanchSummary';
import RanchSummaryTableHeader from '../RanchSummaryTableHeader';
import useGenerateRanchSummaryTableGrapeRows, {
  GrapeMeasureEnum,
  RanchSummaryTableGrapeRow,
  SelectedMeasureMap,
} from './hooks/useGenerateRanchSummaryTableGrapeRows';
import { generateExportColumnsGrapes } from './utils/generateExportColumnsGrapes';
import { getDisplayedGrapeValue } from './utils/getDisplayedGrapeValue';
export enum GrapeColumnEnum {
  scanDate = 'scanDate',
  ranchName = 'ranchName',
  blockName = 'blockName',
  variety = 'variety',
  buds = 'buds',
  earlyShoots = 'earlyShoots',
  shoots = 'shoots',
  earlyClusters = 'earlyClusters',
  clusters = 'clusters',
  berries = 'berries',
}

const hiddenColumns: string[] = [
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.totalCount}`,
];

const exportColumns: string[] = [
  GrapeColumnEnum.scanDate,
  GrapeColumnEnum.ranchName,
  GrapeColumnEnum.blockName,
  GrapeColumnEnum.variety,
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.buds}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.earlyShoots}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.length}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.earlyClusters}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.clusters}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.perMeter}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.perPlant}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.totalCount}`,
  `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.length}`,
];

interface Props {
  blocksFiltered: Block[] | undefined;
}

const RanchSummaryTableGrape: React.FC<Props> = ({ blocksFiltered }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const units = useUnits();
  const isLoadingCustomerSummary = useAppSelector(
    selectIsLoadingCustomerSummary
  );
  const clickedRowScanIds = useAppSelector(selectRanchSummaryClickedRowScanIDs);
  const tableMeasure = useAppSelector(selectRanchSummaryTableMeasure);
  const currTableMeasure = SelectedMeasureMap[tableMeasure];
  const currCustomerID = useAppSelector(selectCurrCustomerID);

  // Get varieties for the current customer to support variety name lookup
  const { varieties } = useVarietiesByCustomer(currCustomerID);

  const rows = useGenerateRanchSummaryTableGrapeRows(blocksFiltered, varieties);

  const handleBlockDetailClick = useCallback(
    (ranchID: string, blockID: string, scanID: string) => {
      dispatch(setCurrRanchByID(ranchID));
      dispatch(setCurrBlockByID(blockID));
      dispatch(setCurrScanByID(scanID));
      dispatch(setRanchPageView(RanchPageViewEnum.blockDetail));
      dispatch(addRanchSummaryClickedRowScanID(scanID));
    },
    [dispatch]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: GrapeColumnEnum.scanDate,
        headerName: t('Date'),
        type: 'string',
        renderCell: (params) => (
          <span
            style={{
              color: clickedRowScanIds.includes(params.row.scanID)
                ? theme.colors.canopy.content.link.visited
                : theme.colors.canopy.content.link.default,
              cursor: 'pointer',
            }}
            onClick={() =>
              handleBlockDetailClick(
                params.row.ranchID,
                params.row.blockID,
                params.row.scanID
              )
            }
          >
            {params.value}
          </span>
        ),
      },
      {
        field: GrapeColumnEnum.ranchName,
        headerName: t('Ranch'),
        type: 'string',
      },
      {
        field: GrapeColumnEnum.blockName,
        headerName: t('Block'),
        type: 'string',
        sortComparator: (v1: string, v2: string) =>
          v1.localeCompare(v2, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
      },
      {
        field: GrapeColumnEnum.variety,
        headerName: t('Variety'),
        type: 'string',
      },
      {
        field: GrapeColumnEnum.buds,
        headerName: t('Buds'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.buds
          ),
      },
      ...generateExportColumnsGrapes(GrapeColumnEnum.buds, 'Buds', units, t),
      {
        field: GrapeColumnEnum.earlyShoots,
        headerName: t('Early Shoots'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.earlyShoots
          ),
      },
      ...generateExportColumnsGrapes(
        GrapeColumnEnum.earlyShoots,
        'Early Shoots',
        units,
        t
      ),
      {
        field: GrapeColumnEnum.shoots,
        headerName: t('Shoots'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.shoots
          ),
      },
      ...generateExportColumnsGrapes(
        GrapeColumnEnum.shoots,
        'Shoots',
        units,
        t
      ),
      {
        field: `${GrapeColumnEnum.shoots}-${GrapeMeasureEnum.length}`,
        headerName: `${t('Shoot Length')} (${getUnitSymbol(
          units,
          MeasurementTypes.millimeterOrInch,
          t
        )})`,
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            GrapeMeasureEnum.length,
            GrapeColumnEnum.shoots
          ),
      },
      ...generateExportColumnsGrapes(
        GrapeColumnEnum.earlyClusters,
        'Early Clusters',
        units,
        t
      ),
      {
        field: GrapeColumnEnum.earlyClusters,
        headerName: t('Early Clusters'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.earlyClusters
          ),
      },
      {
        field: GrapeColumnEnum.clusters,
        headerName: t('Clusters'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.clusters
          ),
      },
      ...generateExportColumnsGrapes(
        GrapeColumnEnum.clusters,
        'Clusters',
        units,
        t
      ),
      {
        field: GrapeColumnEnum.berries,
        headerName: t('Berries'),
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            currTableMeasure,
            GrapeColumnEnum.berries
          ),
      },
      ...generateExportColumnsGrapes(
        GrapeColumnEnum.berries,
        'Berries',
        units,
        t
      ),
      {
        field: `${GrapeColumnEnum.berries}-${GrapeMeasureEnum.length}`,
        headerName: `${t('Berry Width')} (mm)`, // Always in mm, industry standard
        type: 'number',
        valueGetter: (value, row) =>
          getDisplayedGrapeValue(
            row as RanchSummaryTableGrapeRow,
            GrapeMeasureEnum.length, // Width is stored in the length field
            GrapeColumnEnum.berries
          ),
      },
    ],
    [handleBlockDetailClick, t, currTableMeasure, units, clickedRowScanIds]
  );

  return (
    <GenericDataGrid
      rows={rows as GridRowsProp}
      columns={columns}
      loading={isLoadingCustomerSummary}
      checkboxSelection
      disableRowSelectionOnClick
      toolbar={RanchSummaryTableHeader}
      selectedRowCountDispatchFunction={setRanchSummaryTableNumRowsSelected}
      noRowsOverlay={NoRowsOverlayRanchSummary}
      hiddenColumns={hiddenColumns}
      csvOptions={{
        allColumns: true,
        fields: exportColumns,
        fileName: createExportFileName('Grape Ranch Summary', t, units),
        utf8WithBom: true,
      }}
    />
  );
};

export default RanchSummaryTableGrape;
